import { useEffect, useMemo, useRef, useState } from "react";
import type { Order } from "@/lib/projects";
import { fetchActualHoursBatch } from "@/pages/projekte/hoursUsage";

export interface BookedHoursEntry {
  hours: number | null;
  loading: boolean;
  error: boolean;
}

/**
 * Lädt gebuchte Stunden (Hero + Nachunternehmer) für die Projekttabelle,
 * sobald die Spalte sichtbar ist.
 */
export function useBookedHours(
  orders: Order[],
  enabled: boolean,
): Record<number, BookedHoursEntry> {
  const [byId, setById] = useState<Record<number, BookedHoursEntry>>({});
  const resolvedRef = useRef<Map<number, BookedHoursEntry>>(new Map());
  const runIdRef = useRef(0);
  const ordersRef = useRef(orders);
  ordersRef.current = orders;

  const orderIdsKey = useMemo(
    () =>
      orders
        .filter((order) => order.id > 0)
        .map((order) => order.id)
        .sort((a, b) => a - b)
        .join(","),
    [orders],
  );

  useEffect(() => {
    if (!enabled || !orderIdsKey) {
      setById({});
      return;
    }

    let cancelled = false;
    const runId = ++runIdRef.current;

    void (async () => {
      const ordersSnapshot = ordersRef.current;
      if (!ordersSnapshot.length) return;

      const initial: Record<number, BookedHoursEntry> = {};
      const toFetch: Order[] = [];

      for (const order of ordersSnapshot) {
        if (!order.id || order.id <= 0) continue;
        const cached = resolvedRef.current.get(order.id);
        if (cached && !cached.loading) {
          initial[order.id] = cached;
          continue;
        }
        initial[order.id] = { hours: null, loading: true, error: false };
        toFetch.push(order);
      }

      if (!cancelled && runId === runIdRef.current) {
        setById((prev) => ({ ...prev, ...initial }));
      }

      if (toFetch.length === 0) return;

      const actualByOrder = await fetchActualHoursBatch(toFetch);
      if (cancelled || runId !== runIdRef.current) return;

      const updates: Record<number, BookedHoursEntry> = {};
      for (const order of toFetch) {
        const actual = actualByOrder.get(order.id) ?? { actualHours: null, error: true };
        const entry: BookedHoursEntry = {
          hours: actual.error ? null : actual.actualHours,
          loading: false,
          error: actual.error,
        };
        resolvedRef.current.set(order.id, entry);
        updates[order.id] = entry;
      }
      setById((prev) => ({ ...prev, ...updates }));
    })();

    return () => {
      cancelled = true;
    };
  }, [orderIdsKey, enabled]);

  return byId;
}
